import React, { useState, useMemo } from 'react';
import { 
    Box, 
    Card, 
    CardContent, 
    TextField, 
    Typography, 
    Grid, 
    Alert,
    InputAdornment,
    Avatar,
    Pagination,
    Snackbar,
    IconButton
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { CutterHistory } from '../../types/cutterHistory';
import { grey, blue } from '@mui/material/colors';
import { Contexte, useAuthenticationValue } from '@lectra/react-authentication';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

export interface CutterDetailsHistoryContactProps {
    cutter: CutterHistory;
}

export const CutterDetailsHistoryContact: React.FC<CutterDetailsHistoryContactProps> = ({ cutter }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const itemsPerPage = 9;
    
    // Snackbar state for copy notifications
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    
    // Get current authenticated user for Teams call
    const user: Contexte = useAuthenticationValue();

    // Extract contacts from cutter
    const contacts = cutter.contacts;

    // Copy to clipboard handler
    const handleCopy = async (text: string, message: string): Promise<void> => {
        try {
            await navigator.clipboard.writeText(text);
            setSnackbarMessage(message);
            setSnackbarOpen(true);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    // Filter contacts based on firstname and lastname from the 'name' field
    const filteredContacts = useMemo(() => {
        if (!searchTerm.trim()) {
            return contacts;
        }
        
        const lowerSearchTerm = searchTerm.toLowerCase();
        return contacts.filter(contact => {
            const name = contact.name?.toLowerCase() || '';
            return name.includes(lowerSearchTerm);
        });
    }, [contacts, searchTerm]);

    // Calculate pagination
    const totalPages = Math.ceil(filteredContacts.length / itemsPerPage);
    
    // Display contacts for current page
    const displayedContacts = useMemo(() => {
        const startIndex = (page - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return filteredContacts.slice(startIndex, endIndex);
    }, [filteredContacts, page]);

    // Reset to page 1 when search term changes
    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        setSearchTerm(e.target.value);
        setPage(1);
    };

    // Handle page change
    const handlePageChange = (event: React.ChangeEvent<unknown>, value: number): void => {
        setPage(value);
    };

    // Get initials for avatar
    const getInitials = (name: string): string => {
        if (!name) return '?';
        const parts = name.trim().split(' ');
        if (parts.length >= 2) {
            return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        }
        return name.substring(0, 2).toUpperCase();
    };

    // Get gender color
    const getGenderColor = (gender: string): string => {
        switch (gender?.toLowerCase()) {
            case 'male':
                return blue[500];
            case 'female':
                return '#e91e63';
            default:
                return grey[500];
        }
    };

    return (
        <Box sx={{ p: 2 }}>
            {/* Search Box */}
            <Box sx={{ mb: 3 }}>
                <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Filter by first name or last name..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: 'text.secondary' }} />
                                </InputAdornment>
                            )
                        }
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: 'action.hover',
                        }
                    }}
                />
            </Box>

            {/* Pagination */}
            {filteredContacts.length > itemsPerPage && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
                    <Pagination 
                        count={totalPages} 
                        size="small"
                        color="secondary"
                        page={page} 
                        onChange={handlePageChange}
                    />
                </Box>
            )}

            {/* Contacts Grid */}
            {displayedContacts.length === 0 ? (
                <Alert severity="info">
                    {contacts.length === 0 
                        ? 'No contacts available for this cutter.' 
                        : 'No contacts match your search criteria.'}
                </Alert>
            ) : (
                <Grid container spacing={1.5}>
                    {displayedContacts.map((contact, index) => {
                        const isInternalContact = contact.email?.toLowerCase().endsWith('lectra.com');
                        
                        return (
                        <Grid size={{ xs: 12, md: 4, sm: 6}}  key={`${contact.email}-${index}`}>
                            <Card 
                                sx={{ 
                                    height: '100%',
                                    transition: 'all 0.2s ease',
                                    backgroundColor: (theme) => theme.palette.mode === 'light' 
                                        ? 'background.paper' 
                                        : '#252525',
                                    border: isInternalContact ? '2px solid' : 'none',
                                    borderColor: isInternalContact ? 'secondary.main' : 'transparent',
                                    '&:hover': {
                                        boxShadow: 2,
                                        transform: 'translateY(-2px)'
                                    }
                                }}
                            >
                                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                                    {/* Header with Avatar and Name */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        <Avatar 
                                            sx={{ 
                                                bgcolor: getGenderColor(contact.gender),
                                                mr: 1,
                                                width: 32,
                                                height: 32,
                                                fontSize: '0.75rem',
                                                fontWeight: 600
                                            }}
                                        >
                                            {getInitials(contact.name)}
                                        </Avatar>
                                        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <Typography 
                                                variant="body1" 
                                                sx={{ 
                                                    fontWeight: 600,
                                                    fontSize: '0.875rem',
                                                    color: 'text.primary',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}
                                            >
                                                {contact.name || 'Unknown'}
                                            </Typography>
                                            {contact.name && (
                                                <IconButton 
                                                    onClick={() => { handleCopy(contact.name, 'Name copied!').catch(() => {}); }}
                                                    sx={{ p: 0, minWidth: 'auto' }}
                                                    size="small"
                                                >
                                                    <ContentCopyIcon sx={{ fontSize: '0.75rem', color: 'action.active' }} />
                                                </IconButton>
                                            )}
                                        </Box>
                                    </Box>

                                    {/* Contact Details */}
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                        {/* Job Function */}
                                        {contact.jobFunction && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem', flex: 1 }}>
                                                    {contact.jobFunction}
                                                </Typography>
                                                <IconButton 
                                                    onClick={() => { handleCopy(contact.jobFunction, 'Job function copied!').catch(() => {}); }}
                                                    sx={{ p: 0, minWidth: 'auto' }}
                                                    size="small"
                                                >
                                                    <ContentCopyIcon sx={{ fontSize: '0.65rem', color: 'action.active' }} />
                                                </IconButton>
                                            </Box>
                                        )}

                                        {/* Email */}
                                        {contact.email && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Typography 
                                                    variant="body2" 
                                                    component="a"
                                                    href={`mailto:${contact.email}?subject=${encodeURIComponent(`${cutter.state.harD_Machine_type} ${cutter.serialNumber}`)}`}
                                                    sx={{ 
                                                        color: blue[600],
                                                        fontSize: '0.75rem',
                                                        textDecoration: 'none',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                        flex: 1,
                                                        '&:hover': {
                                                            textDecoration: 'underline'
                                                        }
                                                    }}
                                                >
                                                    {contact.email}
                                                </Typography>
                                                <IconButton 
                                                    onClick={() => { handleCopy(contact.email, 'Email copied!').catch(() => {}); }}
                                                    sx={{ p: 0, minWidth: 'auto' }}
                                                    size="small"
                                                >
                                                    <ContentCopyIcon sx={{ fontSize: '0.65rem', color: 'action.active' }} />
                                                </IconButton>
                                            </Box>
                                        )}

                                        {/* Phone - MS Teams Call */}
                                        {(contact.phone || contact.mobilePhone) && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Typography 
                                                    variant="body2" 
                                                    component="a"
                                                    href={`https://teams.microsoft.com/l/call/0/0?users=${user?.user?.email ?? ''},${contact.email}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    sx={{ 
                                                        color: blue[600],
                                                        fontSize: '0.75rem',
                                                        textDecoration: 'none',
                                                        flex: 1,
                                                        '&:hover': {
                                                            textDecoration: 'underline'
                                                        }
                                                    }}
                                                >
                                                    {contact.phone || contact.mobilePhone}
                                                </Typography>
                                                <IconButton 
                                                    onClick={() => { handleCopy(contact.phone || contact.mobilePhone, 'Phone copied!').catch(() => {}); }}
                                                    sx={{ p: 0, minWidth: 'auto' }}
                                                    size="small"
                                                >
                                                    <ContentCopyIcon sx={{ fontSize: '0.65rem', color: 'action.active' }} />
                                                </IconButton>
                                            </Box>
                                        )}
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                        );
                    })}
                </Grid>
            )}            
            {/* Centralized Snackbar for copy notifications */}
            <Snackbar 
                message={snackbarMessage} 
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                autoHideDuration={1000} 
                onClose={() => setSnackbarOpen(false)} 
                open={snackbarOpen} 
            />        </Box>
    );
};

export default CutterDetailsHistoryContact;
